import { CopyToClipboardIcon, DropdownMenu, Icon } from '@paypalcorp/pp-react'
import React, {
  EventHandler,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import CodeBlock from '../../../components/layout/CodeBlock/CodeBlock'
import styles from '../integration-builder.module.css'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import { SnippetSchemaEnvironmentSampleFile } from '../../../components/integration-builder/code-selector/code-snippets/interfaces'
import withAnalytics from '../../../utils/withAnalytics'
import { CopyButton } from '../../../components/layout'
import { fileTypeOf } from '../../../utils'

import { codePreviewParser } from '../../../components/integration-builder/code-selector/code-snippets/helpers/codePreviewParser'
import templates from '../../../components/integration-builder/code-selector/code-snippets/checkout/templates'

const Code: FunctionComponent = () => {
  type FileOption = {
    value: string
    primaryText: string
  }

  const fptiData = typeof window !== `undefined` && window.fpti
  const [isCopied, setIsCopied] = React.useState(false)

  const [files, setFiles] = useState<SnippetSchemaEnvironmentSampleFile[]>([])
  const [fileOptions, setFileOptions] = useState<FileOption[]>([])
  const [selectedFile, setSelectedFile] = useState('')

  const [selectedLanguage, setSelectedLanguage] = useState('html')
  const [selectedCodeSample, setSelectedCodeSample] = useState('')
  const {
    integrationType,
    snippets = {},
    parameters,
    options,
  } = useContext(CheckoutSimulatorContext)

  const [backendFiles, setBackendFiles] =
    useState<SnippetSchemaEnvironmentSampleFile[]>()
  const [frontendFiles, setFrontendFiles] =
    useState<SnippetSchemaEnvironmentSampleFile[]>()
  const getFallbackTemplate = (file: SnippetSchemaEnvironmentSampleFile) => {
    let templateFileCode = templates.find(
      (template) =>
        template.type === file?.type &&
        template.path === file?.path &&
        template.name === file?.name,
    )

    if (!templateFileCode) {
      setSelectedCodeSample('TEMPLATE NOT FOUND')
      return
    }

    setSelectedCodeSample(
      codePreviewParser(templateFileCode?.content!, { ...parameters, options }),
    )
  }
  const updateCodeSample = (fileName: string) => {
    const file = files.find((file) => file.name === fileName)
    if (!file) return

    const baseUrl = `https://www.paypalobjects.com/devdoc/ib_codesamples/templates`

    let url = `${baseUrl}/${file?.integration}/${file?.environment}/${file?.language}/${file?.name}`

    fetch(url, { method: 'GET', redirect: 'follow' })
      .then((res) => {
        if (res.status === 404) {
          getFallbackTemplate(file)
          throw new Error('CDN template not found')
        }
        return res.text()
      })
      .then((content) => {
        setSelectedCodeSample(
          codePreviewParser(content, { ...parameters, options }),
        )
      })
      .catch((error) => {
        getFallbackTemplate(file)
      })
  }

  useEffect(() => {
    if (!snippets.backend || !snippets.frontend) return
    setBackendFiles(snippets.backend)
    setFrontendFiles(snippets.frontend)

    let file: string

    file =
      [...snippets.frontend, ...snippets.backend].find(
        (snippet) => snippet.name === selectedFile,
      )?.name || ''

    if (!file && snippets.frontend[0]) file = snippets.frontend[0].name
    if (!file && snippets.backend[0]) file = snippets.backend[0].name

    updateCodeSample(file)
    setSelectedFile(file)
    setSelectedLanguage(fileTypeOf(file))
  }, [snippets])

  useEffect(() => {
    if (!frontendFiles || !backendFiles) return
    setFiles([...frontendFiles!, ...backendFiles!])
  }, [backendFiles, frontendFiles, parameters])

  useEffect(() => {
    setFileOptions(
      files.map((file) => ({
        value: file.name,
        primaryText: file.name,
        file,
      })),
    )
    updateCodeSample(selectedFile)
  }, [files])

  const handleOnChangeFile: EventHandler<any> = (event: any) => {
    setSelectedFile(event.target.value)
    setSelectedLanguage(fileTypeOf(event.target.value))
    updateCodeSample(event.target.value)
  }

  const copySampleFile = async (selectedFile: string) => {
    setIsCopied(true)
    try {
      await navigator.clipboard.writeText(selectedCodeSample)
    } catch (err) {
      console.error('Failed to copy to clipboard')
    }
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      const event = `code:${selectedFile}_${
        integrationType === 'standard' ? 'sc' : 'ac'
      }`
      window.PAYPAL.analytics.instance.record({
        data: {
          component: 'devdiscoverynodeweb',
          page: 'main:developer:integration-builder',
          pgrp: 'main:developer:integration-builder',
          e: 'cl',
          link: event,
          event_action: event,
          ...fptiData,
        },
      })
    }
    setTimeout(() => setIsCopied(false), 3000)
  }

  return (
    <div className={styles.checkout_simulator__code_container}>
      <div className={styles.checkout_simulator__file_selection_wrapper}>
        <DropdownMenu
          className={styles.checkout_simulator__file_selector}
          id="fileSelect"
          label="Code Source File"
          options={fileOptions}
          value={selectedFile}
          onChange={handleOnChangeFile}
        />
        <CopyButton onClick={() => copySampleFile(selectedFile)}>
          {isCopied ? (
            <Icon
              className={styles.integration_builder__copyIconSuccess}
              name="checkmark-background"
              color="sysColorSuccessMain"
            />
          ) : (
            <Icon
              className={styles.integration_builder__copyIcon}
              name="copy-to-clipboard"
              color="sysColorPrimaryMain"
            />
          )}
        </CopyButton>
      </div>
      <CodeBlock hasOwnCopyBlock={true} className={selectedLanguage}>
        {selectedCodeSample}
      </CodeBlock>
    </div>
  )
}

export default withAnalytics(Code)
